import type { ReactNode } from 'react';
import { Bandeau } from './Bandeau';
import { Bouton } from './Bouton';
import { Titre } from './Titre';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LES TROIS ÉTATS D'UNE SURFACE QUI AFFICHE DE LA DONNÉE SERVEUR — ET IL N'Y A
 * QU'UNE SEULE TELLE SURFACE SUR TOUT CE SITE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `site/CLAUDE.md` : « toute page qui affiche de la donnée serveur traite **les trois
 * états** : chargement par squelette, erreur avec réessai, et vide avec un message utile.
 * **Une page qui n'en traite qu'un est inachevée.** »
 *
 * **LES TROIS TIENNENT DANS UN SEUL FICHIER ICI, LÀ OÙ `web/` EN A TROIS, ET L'ÉCART A UN
 * MOTIF.** Sur `web/`, cinq écrans consomment ces états séparément, dans des combinaisons
 * différentes ; ils vivent donc en trois primitives indépendantes. **Ici, une seule page
 * lit la base** — l'accueil et son carrousel, `CDC-6` F-69 — et les trois états y sont
 * toujours les trois branches du même affichage. Les séparer donnerait trois fichiers dont
 * **aucun n'aurait plus d'un appelant**, et `design-system-first` ferme cette porte : « un
 * composant naît d'un usage réel ».
 *
 * **CE N'EST PAS UN COMPOSANT PARTAGÉ AVEC `web/`, ET CE N'EST PAS UNE DUPLICATION À
 * CORRIGER.** `CLAUDE.md` : « `web/`, `site/` et `admin/` **ne se parlent jamais entre
 * eux**. Aucun import direct, **aucun composant partagé.** » Ce qui est partagé est
 * `tokens.css`, et lui seul.
 */

/**
 * **Le chargement, par SQUELETTE et non par tourniquet.**
 *
 * Le squelette occupe la place que la donnée prendra, ce qu'un tourniquet ne fait pas : la
 * page ne saute pas quand la donnée arrive. Sur une surface « atteinte une fois, par des
 * gens qui ne connaissent pas le produit », un saut de mise en page est la première chose
 * qu'ils verront du produit.
 */
export function Squelette({ lignes = 1 }: { readonly lignes?: number }) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="flex flex-wrap items-center justify-center gap-6"
    >
      <span className="sr-only">Chargement…</span>
      {Array.from({ length: lignes }, (_, rang) => (
        <div
          key={rang}
          className="h-12 w-32 animate-pulse rounded-2 bg-fond-bloc"
        />
      ))}
    </div>
  );
}

/**
 * **L'erreur, AVEC son réessai.**
 *
 * **Le message vient du serveur quand il en donne un**, et le front ne branche jamais un
 * comportement dessus : `DOCTRINE.md` §5 — « **`code` est le contrat réel** : c'est sur lui
 * que le front branche son comportement, **jamais sur `message`** ».
 *
 * **SUR CETTE SURFACE, L'ERREUR NE DIT RIEN DU PRODUIT NI DE SES CLIENTES.** Une page
 * publique lue par n'importe qui, y compris par des machines, n'a rien à apprendre à
 * personne sur ce qui a échoué en interne.
 */
export function EtatErreur({
  message,
  onReessayer,
}: {
  readonly message: string;
  readonly onReessayer?: () => void;
}) {
  return (
    <Bandeau
      ton="echec"
      action={
        onReessayer && (
          <Bouton variante="douce" taille="petite" onClick={onReessayer}>
            Réessayer
          </Bouton>
        )
      }
    >
      {message}
    </Bandeau>
  );
}

/**
 * **L'état vide, et sur cette surface il ne dit PAS ce qu'il faut faire.**
 *
 * **C'est l'écart avec `web/`, et il est délibéré.** Là-bas, « un état vide dit ce qu'il
 * faut faire », parce que la personne qui le lit est connectée et **peut** faire quelque
 * chose. Ici, l'état vide du carrousel se produit quand « aucune structure n'a ses trois
 * colonnes déclencheuses remplies » (F-70) : **la personne qui le lit n'a aucun geste à
 * faire, et le geste qui le remplirait appartient au compte qui voit tout**, hors de sa
 * portée et hors de sa connaissance.
 *
 * Lui proposer une action serait donc lui montrer une porte fermée. Le bloc dit ce qui est,
 * et rien de plus.
 */
export function EtatVide({
  titre,
  children,
}: {
  readonly titre: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-4 bg-fond-surface px-6 py-10 text-center">
      <Titre niveau={2} taille="20">
        {titre}
      </Titre>
      <p className="max-w-etroite text-16 leading-normal text-texte-faible">
        {children}
      </p>
    </div>
  );
}
